import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, ExternalLink } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";

const copy = (text, what) => navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`));

const SetupAnswers = ({ answers }) => (
  <section data-testid="stripe-setup-answers" className="rounded-2xl p-6 bg-white border border-[#F0EBE1]">
    <h3 className="font-heading font-bold text-lg">Answers for Stripe's setup questions</h3>
    <p className="mt-1 text-sm text-stone-600">Use these when Stripe asks about your business, what you sell, refunds or tax. Tap any answer to copy it.</p>
    <dl className="mt-4 divide-y divide-[#F0EBE1]">
      {Object.entries(answers).map(([q, a], i) => (
        <div key={q} className="py-3 grid sm:grid-cols-[200px_1fr_auto] gap-2 items-start text-sm">
          <dt className="font-medium">{q}</dt>
          <dd className="text-stone-700">{a}</dd>
          <button data-testid={`stripe-answer-copy-${i}`} onClick={() => copy(a, q)} className="justify-self-start p-2 rounded-full hover:bg-[#F0EBE1]" aria-label={`Copy ${q}`}><Copy size={15} /></button>
        </div>
      ))}
    </dl>
  </section>
);

const LinkList = ({ products }) => (
  <section data-testid="stripe-payment-links" className="rounded-2xl p-6 bg-white border border-[#F0EBE1]">
    <h3 className="font-heading font-bold text-lg">Payment links</h3>
    <p className="mt-1 text-sm text-stone-600">One link per product, for sharing in DMs, bios or invoices. Orders paid by link show up in your Orders tab.</p>
    <ul className="mt-4 divide-y divide-[#F0EBE1] text-sm">
      {products.filter((p) => p.active).map((p) => (
        <li key={p.slug} data-testid={`stripe-product-${p.slug}`} className="py-2.5 flex items-center gap-3">
          <span className="flex-1 min-w-0 truncate">{p.name}</span>
          <span className="text-stone-500">{money(p.price)}</span>
          {p.link_url ? (
            <>
              <button data-testid={`stripe-link-copy-${p.slug}`} onClick={() => copy(p.link_url, "Payment link")} className="px-3 py-1 rounded-full border border-[#E7E0D3] hover:bg-[#F0EBE1] inline-flex items-center gap-1"><Copy size={13} /> Copy link</button>
              <a data-testid={`stripe-link-open-${p.slug}`} href={p.link_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-full hover:bg-[#F0EBE1]" aria-label={`Open ${p.name} link`}><ExternalLink size={14} /></a>
            </>
          ) : <span className="text-amber-700">Not in Stripe yet</span>}
        </li>
      ))}
    </ul>
  </section>
);

export const AdminStripeSetup = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/stripe/status").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d?.job?.running) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [d, load]);
  const sync = () => api.post("/admin/stripe/sync").then(load).catch((e) => toast.error(errMsg(e)));
  if (!d) return <p className="mt-8 text-stone-500">Loading…</p>;
  const inStripe = d.products.filter((p) => p.active && p.in_stripe).length;
  const active = d.products.filter((p) => p.active).length;
  return (
    <div data-testid="stripe-setup" className="mt-8 grid gap-6">
      <section className="rounded-2xl p-6 bg-[#1C1917] text-white flex flex-wrap items-center gap-5 justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-400">{d.mode === "live" ? "Live Stripe account" : "Stripe test mode"}</p>
          <h3 data-testid="stripe-sync-count" className="font-heading font-bold text-2xl mt-1">{inStripe} of {active} products in Stripe</h3>
          <p data-testid="stripe-sync-status" className="mt-1 text-sm text-stone-300">
            {d.job.running ? "Copying products to Stripe… this takes about a minute." : d.job.finished_at ? `Last synced ${new Date(d.job.finished_at).toLocaleString()}${d.job.failed ? ` · ${d.job.failed} failed` : ""}${d.job.error ? ` · ${d.job.error}` : ""}` : "Not synced yet."}
          </p>
        </div>
        <button data-testid="stripe-sync-button" disabled={d.job.running} onClick={sync} className="px-5 py-3 rounded-full bg-[#E06D44] hover:bg-[#c95a33] disabled:opacity-60 inline-flex items-center gap-2 font-medium transition-colors">
          <RefreshCw size={16} className={d.job.running ? "animate-spin" : ""} /> {d.job.finished_at ? "Sync again" : "Copy products to Stripe"}
        </button>
      </section>
      <p className="text-sm text-stone-600 -mt-2">Your website checkout already works without this. Syncing fills Stripe's product catalog, sets each product's tax type, and makes a payment link for each one. Run it again after changing prices or adding products.</p>
      <SetupAnswers answers={d.answers} />
      <LinkList products={d.products} />
    </div>
  );
};
